import { useCallback, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./PhotoGallery.scss";

const SortablePhotoTile = ({ photo, isMain, onSetMain, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: photo.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} className={`pg-tile${isDragging ? " is-dragging" : ""}`}>
      <img src={photo.url} alt="Venue" />
      <div className="pg-drag-handle" {...attributes} {...listeners} aria-label="Drag to reorder">
        <Icon icon="material-symbols:drag-indicator" width={18} />
      </div>
      <div className="pg-tile-actions">
        {!isMain && (
          <button type="button" className="pg-tile-action-btn" title="Set as cover" onClick={() => onSetMain(photo.id)}>
            <Icon icon="material-symbols:star-outline-rounded" width={18} />
          </button>
        )}
        <button type="button" className="pg-tile-action-btn is-danger" title="Delete photo" onClick={() => onRemove(photo.id)}>
          <Icon icon="material-symbols:delete-outline-rounded" width={18} />
        </button>
      </div>
      {isMain && (
        <span className="pg-main-badge">
          <Icon icon="material-symbols:star-rounded" width={12} />
          Cover
        </span>
      )}
    </div>
  );
};

const PhotoGallery = ({
  photos,
  onFilesPicked,
  onReorder,
  onSetMain,
  onRemove,
  onRetry,
  onDiscard,
  helperText = "PNG, JPG, or GIF. First photo is the cover.",
}) => {
  const [dropZoneActive, setDropZoneActive] = useState(false);
  const fileInputRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const confirmed = photos.filter((p) => p.status === "confirmed");
  const pending = photos.filter((p) => p.status !== "confirmed");

  const handleDropZoneClick = useCallback(() => fileInputRef.current?.click(), []);

  const handleFileInputChange = useCallback((e) => {
    if (e.target.files?.length) onFilesPicked(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [onFilesPicked]);

  const handleDropZoneDragOver = useCallback((e) => {
    e.preventDefault();
    setDropZoneActive(true);
  }, []);

  const handleDropZoneDragLeave = useCallback(() => setDropZoneActive(false), []);

  const handleDropZoneDrop = useCallback((e) => {
    e.preventDefault();
    setDropZoneActive(false);
    if (e.dataTransfer.files?.length) onFilesPicked(e.dataTransfer.files);
  }, [onFilesPicked]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = confirmed.findIndex((p) => p.id === active.id);
    const newIndex = confirmed.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const nextConfirmed = arrayMove(confirmed, oldIndex, newIndex);
    onReorder([...nextConfirmed, ...pending]);
  }, [confirmed, pending, onReorder]);

  return (
    <div className="pg-root">
      <button
        type="button"
        className={`pg-dropzone${dropZoneActive ? " is-active" : ""}`}
        onClick={handleDropZoneClick}
        onDragOver={handleDropZoneDragOver}
        onDragLeave={handleDropZoneDragLeave}
        onDrop={handleDropZoneDrop}
      >
        <Icon icon="material-symbols:add-photo-alternate-outline-rounded" width={32} />
        <span>Drop photos here or click to upload</span>
        <span className="sub">{helperText}</span>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif"
        multiple
        hidden
        onChange={handleFileInputChange}
      />
      {photos.length > 0 && (
        <div className="pg-grid">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={confirmed.map((p) => p.id)} strategy={rectSortingStrategy}>
              {confirmed.map((photo, index) => (
                <SortablePhotoTile
                  key={photo.id}
                  photo={photo}
                  isMain={index === 0}
                  onSetMain={onSetMain}
                  onRemove={onRemove}
                />
              ))}
            </SortableContext>
          </DndContext>
          {pending.map((photo) => (
            <div className="pg-tile" key={photo.id}>
              <img src={photo.url} alt="Uploading" />
              {photo.status === "uploading" && (
                <div className="pg-upload-overlay">
                  <div className="pg-spinner" />
                  Uploading
                </div>
              )}
              {photo.status === "error" && (
                <div className="pg-error-overlay">
                  <Icon icon="material-symbols:error-outline-rounded" width={22} />
                  Upload failed
                  {onRetry && (
                    <button type="button" onClick={() => onRetry(photo.id)}>Retry</button>
                  )}
                  <button type="button" onClick={() => (onDiscard ?? onRemove)(photo.id)}>Discard</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
